import { createEntityAdapter, createSlice, EntityId } from '@reduxjs/toolkit';
import { RootState } from "../index";
import {
  fetchServerPlan,
  fetchServerPlans,
  createServerPlan,
  updateServerPlan,
  reorderServerPlans
} from "src/actions/mine_craft/serverPlansActions";

export interface ServerPlanInterface {
  id: number;
  app_id: number;

  slug: string;
  title: string;
  description: string;

  order: number;
  published: boolean;
  featured: boolean;

  coins: number;

  memory_mb: number;
  disk_mb: number;
  cpu_percent: number;
  max_players: number;

  features: string[];

  primary_color: string | null;
  tint_color: string | null;

  created_at: string;
  updated_at: string;
}

export interface initialInterface {
  error: string | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  count: number;

  editId: EntityId | null;
  editError: string | null;

  newParams: ServerPlanInterface | undefined;
  newError: string | null;
}

const initialState: initialInterface = {
  error: null,
  status: 'idle',
  count: 0,

  editId: null,
  editError: null,

  newParams: undefined,
  newError: null,
};

const adaptor = createEntityAdapter<ServerPlanInterface>();

const slice = createSlice({
  name: 'serverPlans',
  initialState: adaptor.getInitialState(initialState),
  reducers: {
    setStatus: (state, action) => {
      state.status = action.payload;
    },
    setNewParams: (state, action) => {
      state.newParams = action.payload || undefined;
      state.newError  = initialState.newError;
    },
    setNewError: (state, action) => {
      state.newError = action.payload;
    },
    setEditId: (state, action) => {
      state.editId    = action.payload;
      state.editError = initialState.editError;
    },
    setEditError: (state, action) => {
      state.editError = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // #index
      .addCase(fetchServerPlans.pending, (state, _action) => {
        state.status = 'loading'
      })
      .addCase(fetchServerPlans.fulfilled, (state, action) => {
        state.count = action.payload.count;
        state.entities = action.payload.serverPlans;
        state.ids = action.payload.ids;
        state.status = 'succeeded';
      })
      .addCase(fetchServerPlans.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Unknown Error';
      })

      // #create
      .addCase(createServerPlan.fulfilled, (state, action) => {
        const serverPlan = Object.values(action.payload.serverPlan)[0];
        state.entities[serverPlan.id] = serverPlan;
      })
      .addCase(createServerPlan.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.newError = error || action.error.message || 'Unknown Error';
      })

      // #show
      .addCase(fetchServerPlan.fulfilled, (state, action) => {
        const serverPlan = Object.values(action.payload.serverPlan)[0];
        state.entities[serverPlan.id] = serverPlan;
      })

      // #update
      .addCase(updateServerPlan.fulfilled, (state, action) => {
        const serverPlan = Object.values(action.payload.serverPlan)[0];
        state.entities[serverPlan.id] = serverPlan;
      })
      .addCase(updateServerPlan.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.editError = error || action.error.message || 'Unknown Error';
      })

      // #reorder
      .addCase(reorderServerPlans.fulfilled, (state, action) => {
        state.ids = action.payload.ids;
      })
  }
});

const reducer = slice.reducer;
export default reducer;

export const {
  setEditId,
  setNewParams,
  setNewError,
  setEditError,
} = slice.actions;

export const {
  selectById: selectServerPlanById,
  selectIds: selectServerPlanIds,
  selectEntities: selectServerPlanEntities,
} = adaptor.getSelectors<RootState>(state => state.mineCraft.serverPlans);
